import { useState, useRef, useEffect } from 'react'
import { X, Send, Sparkles, Minimize2 } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/store/uiStore'
import { useChatStore } from '@/store/chatStore'
import { Button } from '@/components/ui/Button'

export function AIAssistantBubble() {
  const { chatOpen, toggleChat, setChatOpen } = useUIStore()
  const { messages, sendMessage, isStreaming, clearMessages } = useChatStore()
  const [input, setInput] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (chatOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
      inputRef.current?.focus()
    }
  }, [chatOpen, messages])

  const handleSend = async () => {
    const text = input.trim()
    if (!text || isStreaming) return
    setInput('')
    try {
      await sendMessage(text)
    } catch {
      // error shown in chat
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void handleSend()
    }
  }

  const suggestedQuestions = [
    'Why aren\'t I getting sales?',
    'How do I set up Meta Pixel?',
    'What apps do I need?',
    'How to handle a chargeback?',
  ]

  return (
    <>
      {/* Floating Button */}
      <AnimatePresence>
        {!chatOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            onClick={() => setChatOpen(true)}
            className="fixed bottom-20 right-4 md:bottom-6 md:right-6 z-50 w-14 h-14 rounded-full bg-ai shadow-glow-ai flex items-center justify-center hover:bg-ai-hover transition-colors"
          >
            <Sparkles className="w-6 h-6 text-white" />
            {messages.length > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-error text-white text-xs flex items-center justify-center font-bold">
                {messages.filter((m) => m.role === 'assistant').length}
              </span>
            )}
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Panel */}
      <AnimatePresence>
        {chatOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: 'spring', duration: 0.4 }}
            className="fixed bottom-20 right-4 md:bottom-6 md:right-6 z-50 w-[calc(100vw-2rem)] max-w-sm h-[520px] flex flex-col rounded-2xl border border-ai/30 bg-surface shadow-glow-ai overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-ai/10 border-b border-ai/20">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-ai flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">AI Assistant</p>
                  <p className="text-xs text-muted">Powered by Claude</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={clearMessages}
                  className="text-xs text-muted hover:text-foreground transition-colors"
                >
                  Clear
                </button>
                <button onClick={() => setChatOpen(false)} className="text-muted hover:text-foreground">
                  <Minimize2 className="w-4 h-4" />
                </button>
                <button onClick={toggleChat} className="text-muted hover:text-foreground">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.length === 0 ? (
                <div className="space-y-3">
                  <p className="text-sm text-muted text-center">
                    Ask me anything about your store!
                  </p>
                  <div className="space-y-2">
                    {suggestedQuestions.map((q) => (
                      <button
                        key={q}
                        onClick={() => { setInput(q); inputRef.current?.focus() }}
                        className="w-full text-left text-xs px-3 py-2 rounded-xl border border-border hover:border-ai/50 hover:bg-ai/5 text-muted hover:text-foreground transition-all"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={cn('flex', msg.role === 'user' ? 'justify-end' : 'justify-start')}
                  >
                    {msg.role === 'assistant' && (
                      <div className="w-6 h-6 rounded-full bg-ai flex items-center justify-center mr-2 mt-0.5 flex-shrink-0">
                        <Sparkles className="w-3 h-3 text-white" />
                      </div>
                    )}
                    <div
                      className={cn(
                        'max-w-[80%] rounded-xl px-3 py-2 text-sm',
                        msg.role === 'user'
                          ? 'bg-primary text-white'
                          : 'bg-surface-2 text-foreground',
                        msg.isStreaming && 'after:content-["▋"] after:animate-pulse after:text-ai'
                      )}
                    >
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-3 border-t border-border">
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask anything..."
                  className="flex-1 rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted focus:border-ai focus:outline-none focus:ring-1 focus:ring-ai"
                  disabled={isStreaming}
                />
                <Button
                  size="sm"
                  variant="ai"
                  onClick={() => void handleSend()}
                  disabled={!input.trim() || isStreaming}
                  isLoading={isStreaming}
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
